import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Loading from "../../components/loading";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";
import {
  getCategories,
  addCategory,
  updateCategory,
  deleteCategory,
} from "../../services/services";
import { X } from "lucide-react";
import { confirmDelete } from "../../components/confirmDelete";
import DataTable from "react-data-table-component";

const ExpensesCategory = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newCategory, setNewCategory] = useState({
    name: "",
    status: "active",
  });
  const [nameError, setNameError] = useState(false);

  const fetchExpenseCategories = async () => {
    const payload = { type: "expense" };
    try {
      setLoading(true);
      const res = await getCategories(payload);
      if (res.data.status) {
        setCategories(res.data.response || []);
      }
    } catch {
      toast.error("Failed to load expense categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenseCategories();
  }, []);

  const handleSaveCategory = async () => {
    if (!newCategory.name.trim()) {
      setNameError(true);
      return toast.error("Category name is required");
    }

    const payload = {
      name: newCategory.name.trim(),
      type: "expense",
      status: newCategory.status,
    };

    try {
      if (editingId) {
        await updateCategory(editingId, payload);
        toast.success("Expense category updated");
      } else {
        await addCategory(payload);
        toast.success("Category added");
      }

      setNewCategory({ name: "", status: "active" });
      setShowAddModal(false);
      setEditingId(null);
      fetchExpenseCategories();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save category");
    }
  };

  const handleEdit = (cat) => {
    setEditingId(cat._id);
    setNewCategory({ name: cat.name, status: cat.status });
    setShowAddModal(true);
  };

  const handleDelete = async (id, name) => {
    const isConfirm = await confirmDelete(`Expense Category - ${name}`);
    if (!isConfirm) return;

    try {
      await deleteCategory(id);
      toast.success("Category deleted");
      fetchExpenseCategories();
    } catch (err) {
      toast.error("Cannot delete — used in expenses");
    }
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingId(null);
    setNewCategory({ name: "", status: "active" });
  };

  // ✅ DataTable Styles
  const customStyles = {
    headCells: {
      style: {
        backgroundColor: "#0b2b6d",
        color: "#fff",
        fontWeight: "600",
        textTransform: "uppercase",
        fontSize: "13px",
      },
    },
  };

  // ✅ DataTable Columns
  const columns = [
    {
      name: "S. No.",
      cell: (row, index) => index + 1,
      width: "80px",
    },
    {
      name: "Category Name",
      selector: (row) => row.name,
      sortable: true,
      // grow: 2,
      wrap: true,
    },
    {
      name: "Status",
      cell: (row) => (
        <span
          className={`badge rounded-pill ${
            row.status === "active" ? "bg-success" : "bg-danger"
          }`}
          style={{ fontSize: "11px" }}
        >
          {row.status.toUpperCase()}
        </span>
      ),
    },
    {
      name: "Action",
      cell: (row) => (
        <div className="d-flex gap-1">
          {/* Edit Button */}
          <button
            className="btn btn-light btn-sm border"
            onClick={() => handleEdit(row)}
          >
            <i className="bi bi-pencil"></i>
          </button>

          {/* Delete Button */}
          <button
            className="btn btn-light btn-sm border text-danger"
            onClick={() => handleDelete(row._id, row.name)}
          >
            <i className="bi bi-trash"></i>
          </button>
        </div>
      ),
    },
  ];

  if (loading) return <Loading />;

  return (
    <>
      <div id="main">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="custom-title mb-0">Expense Categories</h4>
          <button
            className="btn btn-primary"
            onClick={() => setShowAddModal(true)}
          >
            Add Category
          </button>
        </div>

        <div className="card shadow-sm">
          <div className="card-body p-0 rounded">
            <DataTable
              columns={columns}
              data={categories}
              customStyles={customStyles}
              highlightOnHover
              striped
              responsive 
              noDataComponent={
                <div className="text-center p-5 text-muted">
                  <i className="bi bi-inbox display-1"></i>
                  <p className="mt-3">No expense categories found</p>
                </div>
              }
            />
          </div>
        </div>
      </div>

      {/* Modal */}
      {showAddModal && (
        <div
          className="modal d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-0 pb-2">
                <h5 className="modal-title fw-bold">
                  {editingId ? "Edit Expense Category" : "Add Expense Category"}
                </h5>
                <X
                  onClick={closeModal}
                  size={24}
                  style={{ cursor: "pointer" }}
                />
              </div>

              <div className="modal-body pt-2">
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Category Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${nameError ? "is-invalid" : ""}`}
                    value={newCategory.name}
                    onChange={(e) =>
                      setNewCategory({ ...newCategory, name: e.target.value })
                    }
                  />
                  {nameError && (
                    <div className="invalid-feedback">
                      Category name is required
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Status</label>
                  <select
                    className="form-select"
                    value={newCategory.status}
                    onChange={(e) =>
                      setNewCategory({ ...newCategory, status: e.target.value })
                    }
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="modal-footer border-0 pt-2">
                <button className="btn btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveCategory}
                >
                  {editingId ? "Update" : "Add"} Category
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default headerLayoutWrapper(ExpensesCategory);
